import { useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
// components
import Iconify from '../../components/Iconify';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
// sections
import HealthInsuranceVerificationDialog from './HealthInsuranceVerificationDialog';
import EmailAccountCreateDialog from './EmailAccountCreateDialog';
import { MessagePopover, MessageDialog } from './';

export default function NextStepsCard({
  healthInsuranceVerificationRequired = false,
  healthInsuranceVerified = false,
  userName = '',
  companyId,
  companyName,
  contractSigned = false,
  contractSigningUrl = null,
  onHealthInsuranceVerified,
  checkVerificationStatus,
  onEmailAccountCreated,
}) {
  const smUp = useResponsive('up', 'sm');
  const { user, getUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [openEmailAccountDialog, setOpenEmailAccountDialog] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isCheckingEmailStatus, setIsCheckingEmailStatus] = useState(false);

  const contractInfoSubmitted = Boolean(user?.contract_info_submitted || false);
  const hasMigaduAccount = Boolean(user?.has_migadu_account || false);

  const handleVerificationClick = async () => {
    if (checkVerificationStatus) {
      setIsCheckingStatus(true);
      try {
        const isVerified = await checkVerificationStatus();
        if (isVerified) {
          enqueueSnackbar('이미 건강보험 인증이 완료되었습니다', { variant: 'info' });
          return;
        }
      } catch (error) {
        console.error('상태 체크 실패:', error);
      } finally {
        setIsCheckingStatus(false);
      }
    }
    setOpenVerificationDialog(true);
  };

  const handleVerificationClose = () => {
    setOpenVerificationDialog(false);
  };

  const handleVerified = (result) => {
    setOpenVerificationDialog(false);
    if (onHealthInsuranceVerified) {
      onHealthInsuranceVerified(result);
    }
  };

  const handleEmailAccountClick = async () => {
    // 최신 유저 정보 조회 후 이미 계정이 있으면 안내
    setIsCheckingEmailStatus(true);
    try {
      await getUser();
    } catch (error) {
      console.error('유저 정보 조회 실패:', error);
    } finally {
      setIsCheckingEmailStatus(false);
    }

    if (user?.has_migadu_account) {
      enqueueSnackbar('이미 이메일 계정이 생성되어 있습니다', { variant: 'info' });
      return;
    }
    setOpenEmailAccountDialog(true);
  };

  const handleEmailAccountClose = () => {
    setOpenEmailAccountDialog(false);
  };

  const handleEmailAccountCreated = (email) => {
    setOpenEmailAccountDialog(false);
    if (onEmailAccountCreated) {
      onEmailAccountCreated(email);
    }
  };

  return (
    <>
      <Stack sx={{ mb: 3 }} direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography variant="h4" gutterBottom>
            계약이 성사되었습니다
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            아래 절차를 완료하시면 업무를 시작하실 수 있습니다
          </Typography>
          {!smUp && (
            <Box sx={{ mt: 1.5 }}>
              <MessageDialog companyId={companyId} companyName={companyName} />
            </Box>
          )}
        </Stack>
        {smUp && (
          <Box>
            <MessagePopover companyId={companyId} companyName={companyName} />
          </Box>
        )}
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            {/* 1. 계약서 서명 */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {contractSigned ? (
                <Iconify icon="eva:checkmark-circle-fill" sx={{ color: 'success.main', width: 28, height: 28, minWidth: 28 }} />
              ) : (
                <Iconify icon="eva:file-text-fill" sx={{ color: 'secondary.main', width: 28, height: 28, minWidth: 28 }} />
              )}
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: contractSigned ? 'text.disabled' : 'text.primary',
                    textDecoration: contractSigned ? 'line-through' : 'none'
                  }}
                >
                  계약서 서명
                </Typography>
                {!contractSigned && contractSigningUrl && (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      계약서를 확인하고 서명해 주세요
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Iconify icon="eva:edit-2-fill" />}
                        onClick={() => window.open(contractSigningUrl, '_blank', 'noopener,noreferrer')}
                      >
                        계약서에 서명하기
                      </Button>
                    </Box>
                  </>
                )}
                {!contractSigned && !contractSigningUrl && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    카카오톡으로 계약서 링크를 보내드립니다
                  </Typography>
                )}
                {contractSigned && (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      서명 완료
                    </Typography>
                    {contractSigningUrl && (
                      <Box>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          startIcon={<Iconify icon="eva:external-link-fill" />}
                          onClick={() => window.open(contractSigningUrl, '_blank', 'noopener,noreferrer')}
                        >
                          서명한 계약서 보기
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </Stack>

            {/* 2. 이메일 계정 생성 */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {hasMigaduAccount ? (
                <Iconify icon="eva:checkmark-circle-fill" sx={{ color: 'success.main', width: 28, height: 28, minWidth: 28 }} />
              ) : (
                <Iconify icon="eva:email-fill" sx={{ color: 'secondary.main', width: 28, height: 28, minWidth: 28 }} />
              )}
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: hasMigaduAccount ? 'text.disabled' : 'text.primary',
                    textDecoration: hasMigaduAccount ? 'line-through' : 'none'
                  }}
                >
                  이메일 계정 생성
                </Typography>
                {!hasMigaduAccount ? (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      회사와 소통에 사용할 이메일 계정(@diocrew.com)을 생성해주세요
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEmailAccountClick}
                        disabled={isCheckingEmailStatus}
                      >
                        {isCheckingEmailStatus ? '확인 중...' : '계정 생성하기'}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    생성 완료
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* 3. 계약정보 입력 */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {contractInfoSubmitted ? (
                <Iconify icon="eva:checkmark-circle-fill" sx={{ color: 'success.main', width: 28, height: 28, minWidth: 28 }} />
              ) : (
                <Iconify icon="eva:credit-card-fill" sx={{ color: 'secondary.main', width: 28, height: 28, minWidth: 28 }} />
              )}
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: contractInfoSubmitted ? 'text.disabled' : 'text.primary',
                    textDecoration: contractInfoSubmitted ? 'line-through' : 'none'
                  }}
                >
                  계약정보 입력
                </Typography>
                {!contractInfoSubmitted ? (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      급여 수령을 위한 계좌번호와 신분증 사본을 제출해주세요
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.open('/settings?tab=personal', '_blank')}
                        startIcon={<Iconify icon="eva:external-link-fill" />}
                      >
                        입력하러가기
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    입력 완료
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* 4. 건강보험 인증 (required가 true일 때만) */}
            {healthInsuranceVerificationRequired && (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {healthInsuranceVerified ? (
                  <Iconify icon="eva:checkmark-circle-fill" sx={{ color: 'success.main', width: 28, height: 28, minWidth: 28 }} />
                ) : (
                  <Iconify icon="eva:shield-fill" sx={{ color: 'secondary.main', width: 28, height: 28, minWidth: 28 }} />
                )}
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: healthInsuranceVerified ? 'text.disabled' : 'text.primary',
                      textDecoration: healthInsuranceVerified ? 'line-through' : 'none'
                    }}
                  >
                    건강보험 인증으로 경력 확인
                  </Typography>
                  {!healthInsuranceVerified ? (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        신규 계약 시 건강보험공단 자격득실확인서를 통해 경력을 검증합니다
                      </Typography>
                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleVerificationClick}
                          disabled={isCheckingStatus}
                        >
                          {isCheckingStatus ? '확인 중...' : '인증하기'}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      인증 완료
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}

            {/* 안내 메시지 - 건강보험 인증 */}
            {healthInsuranceVerificationRequired && !healthInsuranceVerified && (
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'grey.100',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: .5 }}>
                  <Iconify
                    icon="eva:info-fill"
                    sx={{ color: 'text.secondary', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                  />
                  <Typography variant="subtitle2">
                    건강보험 인증 안내
                  </Typography>
                </Stack>
                <ul style={{ marginLeft: "16px" }}>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      신규 계약 시 경력을 검증하기 위해 건강보험공단 자격득실확인서 인증이 필요합니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      한 번 인증하시면 1년간 재인증이 필요하지 않습니다.
                    </Typography>
                  </li>
                </ul>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <HealthInsuranceVerificationDialog
        open={openVerificationDialog}
        onClose={handleVerificationClose}
        userName={userName}
        onVerified={handleVerified}
        checkVerificationStatus={checkVerificationStatus}
      />

      <EmailAccountCreateDialog
        open={openEmailAccountDialog}
        onClose={handleEmailAccountClose}
        onCreated={handleEmailAccountCreated}
      />
    </>
  );
}
